// Maths for the Overview week's mobile render (mobile overhaul Phase A §2.5;
// mockup §2.1 A `.week` + `.agenda`). The desktop grid's lane/clipping maths
// live in weekLayout.ts; the strip needs none of that — it only asks "which
// kinds touch this day?" and "which items fall in the visible range?" — so
// those two questions are answered here, pure and testable.
//
// A multi-day period (an edit run) covers every day between its bounds, so
// it marks each of those strip cells and still appears once in the agenda.

import { datesOverlap } from '../../shared/dates';
import { DAYS } from './calendar';
import type { CalendarItem, CalendarKind } from './weekLayout';

/** Icon stacking order in a cell, and the agenda's tie-break — the same
    order the desktop legend reads in. */
export const KIND_ORDER: readonly CalendarKind[] = ['shoot', 'edit', 'due', 'hold'];

/** The distinct kinds touching `day`, in KIND_ORDER. One icon per kind, not
    per item: three shoots on one day still read as one camera. */
export function dayKinds(items: CalendarItem[], day: string): CalendarKind[] {
  const present = new Set<CalendarKind>();
  for (const item of items) {
    if (item.start <= day && day <= item.end) present.add(item.kind);
  }
  return KIND_ORDER.filter((kind) => present.has(kind));
}

/** The agenda list: every item overlapping [from, to] — narrowed to one day
    when a strip cell is selected — in start order. Ties fall back to kind
    then name then id so the list never reshuffles between renders. */
export function agendaFor(
  items: CalendarItem[],
  from: string,
  to: string,
  selectedDay: string | null,
): CalendarItem[] {
  const visible = items.filter((item) =>
    selectedDay !== null
      ? item.start <= selectedDay && selectedDay <= item.end
      : datesOverlap(item.start, item.end, from, to),
  );
  return visible.sort(
    (a, b) =>
      a.start.localeCompare(b.start) ||
      KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) ||
      a.name.localeCompare(b.name) ||
      a.id.localeCompare(b.id),
  );
}

/** The agenda's 56px date column: "Thu 10" for a single day, "Tue–Thu" for a
    period (the weekdays are enough context — the strip above carries the
    month). */
export function agendaDateLabel(item: CalendarItem): string {
  const start = new Date(`${item.start}T00:00:00`);
  if (item.end === item.start) return `${DAYS[start.getDay()]} ${start.getDate()}`;
  const end = new Date(`${item.end}T00:00:00`);
  return `${DAYS[start.getDay()]}–${DAYS[end.getDay()]}`;
}
